import React, { useContext, useMemo } from 'react';
import { ANIMATION_DURATION_MS } from '../constants';
import { basePlayerBlock as block } from '../utils';
import {
  ClipSelectVideoPlayerAnimationContextType,
  ClipSelectVideoPlayerAnimationProviderProps,
} from './types';

const ClipSelectVideoPlayerAnimationContext = React.createContext<
  ClipSelectVideoPlayerAnimationContextType | undefined
>(undefined);

export const ClipSelectVideoPlayerAnimationProvider: React.FC<ClipSelectVideoPlayerAnimationProviderProps> = ({
  children,
  isHovered,
  isPlaying,
}) => {
  const value = useMemo(() => {
    // Show progress bar and expanded duration when playing, regardless of hover
    const shouldShowProgressBar = isPlaying || isHovered;
    // Show play button only when hovering
    const shouldShowPlayButton = isHovered;
    // Expand duration tag when playing OR when hovering
    const shouldExpandDurationTag = isPlaying || isHovered;

    return {
      overlay: {
        in: shouldShowPlayButton,
        timeout: ANIMATION_DURATION_MS,
        transitionClassName: block('overlay-transition'),
        appear: true,
      },
      progressBarRow: {
        in: shouldShowProgressBar,
        timeout: ANIMATION_DURATION_MS,
        transitionClassName: block('progress-bar-row-transition'),
        appear: true,
      },
      progress: {
        in: shouldShowProgressBar,
        timeout: ANIMATION_DURATION_MS,
        transitionClassName: block('progress-transition'),
        appear: true,
      },
      durationTagContent: {
        in: shouldExpandDurationTag,
        timeout: ANIMATION_DURATION_MS,
        transitionClassName: block('duration-tag-content-transition'),
        appear: true,
      },
      dataAttributes: {
        'data-hovered': isHovered,
        'data-playing': isPlaying,
      },
    };
  }, [isHovered, isPlaying]);

  return (
    <ClipSelectVideoPlayerAnimationContext.Provider value={value}>
      {children}
    </ClipSelectVideoPlayerAnimationContext.Provider>
  );
};

export function useClipSelectVideoPlayerAnimation() {
  const context = useContext(ClipSelectVideoPlayerAnimationContext);

  if (!context) {
    throw new Error(
      'useClipSelectVideoPlayerAnimation must be used within ClipSelectVideoPlayerAnimationProvider',
    );
  }

  return context;
}
